import ArrSVG from '@public/icons/arr.svg';
import CloseSVG from '@public/icons/close-btn.svg';
import { AxiosError } from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { AnimatePresence, m as motion } from 'framer-motion';
import debounce from 'lodash.debounce';
import { FocusEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { AnyObject, StringSchema, addMethod, array, boolean, lazy, number, object, string } from 'yup';
import { GetCityList } from '@/shared/api/get-city-list';
import { GetCompaniesList } from '@/shared/api/get-companies-list';
import {
    ApiProjectRegistrationRequest,
    AppProjectRegistrationRequest,
    postProjectRegistration,
} from '@/shared/api/post-project-registration';
import { uploadFiles } from '@/shared/api/upload-files';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import Button from '@/shared/components/shared/Button';
import Checkbox from '@/shared/components/shared/Checkbox';
import Input from '@/shared/components/shared/Input';
import PersonalDataConsentLabel from '@/shared/components/shared/PersonalDataConsentLabel';
import Popup from '@/shared/components/shared/Popup';
import Typografed from '@/shared/components/shared/Typografed';
import { DICT } from '@/shared/config/dict';
import {
    TECHNOLOGICAL_MATURITY_TRL_6_9,
    technologicalMaturityOptions,
    themeOptions,
    themeTrackOptions,
    typeOptions,
} from '@/shared/config/optionsData';
import { useGetCurrentUrl } from '@/shared/hooks/use-get-url';
import { useLocale } from '@/shared/hooks/use-locale';
import { useGetUtmParams } from '@/shared/hooks/use-utm/use-get-utm';
import { FeedbackResponse } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { omitEmptyFields } from '@/shared/lib/utils/forms';
import { formatPhoneHref } from '@/shared/lib/utils/strings';
import { isPhone } from '@/shared/lib/utils/validation/phone';
import AutocompleteInput from '../../shared/CityAutocomplete';
import FileDropzone from '../../shared/FileDropzone';
import FormSelect from '../../shared/FormSelect';
import Link from '../../shared/Link';
import Textarea from '../../shared/Textarea';

export const APPLY_REGISTRATION_POPUP_NAME = 'apply-registration-popup';

const HIDE_STEPS_1_AND_2_FOR_DEV = false;

addMethod(string, 'customPhone', function (errorMessage) {
    return this.test(`test-custom-phone`, errorMessage, function (value) {
        return typeof value === 'string' ? isPhone(value) : false;
    });
});

function ApplyRegistrationPopupCombined() {
    const [response, setResponse] = useState<FeedbackResponse | null>(null);
    const [currentStep, setCurrentStep] = useState(HIDE_STEPS_1_AND_2_FOR_DEV ? 3 : 1);
    const { closePopup } = useSetOpenedPopupsState();
    const stepsRef = useRef<HTMLDivElement>(null);
    const formContainerRef = useRef<HTMLDivElement>(null);
    const [selectedType, setSelectedType] = useState(typeOptions[0].value);
    const getUtmParams = useGetUtmParams();
    const currentUrl = useGetCurrentUrl();
    const [showCitySuggestions, setShowCitySuggestions] = useState(false);
    const [showInnSuggestions, setShowInnSuggestions] = useState(false);
    const [citySuggestions, setCitySuggestions] = useState<{ text: string; value: string }[]>([]);
    const [innSuggestions, setInnSuggestions] = useState<{ text: string; value: string; data: any }[]>([]);
    const [isCompanySelected, setIsCompanySelected] = useState(true);

    const locale = useLocale();
    const schema = useMemo(() => {
        if (HIDE_STEPS_1_AND_2_FOR_DEV) {
            return object({
                amount: lazy((value, options) => {
                    const { technologicalMaturity } = options.parent;
                    return technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9
                        ? string().required(DICT.FILL_FIELD[locale])
                        : string().notRequired();
                }),
                customers: string(),
                themeTrack: string().required(DICT.FILL_FIELD[locale]),
                documentUrls: array().of(string()),
                agreementChecked: number().oneOf([1], DICT.FILL_CHECKED[locale]),
                agreementCheckedProcessingPersonalData: number().oneOf([1], DICT.FILL_CHECKED[locale]),
            });
        }

        return object({
            lastname: string().required(DICT.FILL_FIELD[locale]),
            shortName: string().required(DICT.FILL_FIELD[locale]).max(100, DICT.MAX_CHAR_COUNT[locale]),
            theme: string().required(DICT.FILL_FIELD[locale]),
            type: string().required(DICT.FILL_FIELD[locale]),
            technologicalMaturity: string().required(DICT.FILL_FIELD[locale]),
            amount: lazy((value, options) => {
                const { technologicalMaturity } = options.parent;
                return technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9
                    ? string().required(DICT.FILL_FIELD[locale])
                    : string().notRequired();
            }),
            customers: string(),
            name: string().required(DICT.FILL_FIELD[locale]),
            isPhysicalPerson: number(),
            inn: string().when('isPhysicalPerson', {
                is: 1,
                then: (s) => s,
                otherwise: (s) => s.required(DICT.TIN[locale]),
            }),
            resume: string()
                .transform((value) => (typeof value === 'string' ? value.trim() : value))
                .required(DICT.FILL_FIELD[locale])
                .min(30, DICT.MIN_CHAR_COUNT[locale]),
            email: string()
                .required(DICT.FILL_FIELD[locale])
                .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, DICT.INVALID_EMAIL[locale]),
            phone: (
                string().required(DICT.FILL_FIELD[locale]) as StringSchema<string, AnyObject, undefined, ''> & {
                    customPhone: any;
                }
            ).customPhone(DICT.INVALID_PHONE_NUMBER[locale]),
            agreementChecked: number().oneOf([1], DICT.FILL_CHECKED[locale]),
            agreementCheckedProcessingPersonalData: number().oneOf([1], DICT.FILL_CHECKED[locale]),
            themeTrack: string().required(DICT.FILL_FIELD[locale]),
            city: string().required(DICT.FILL_FIELD[locale]),
            presentationUrls: array().of(string().required()),
            documentUrls: array().of(string()),
            submitPrizeDestination: boolean().test(
                'at-least-one-destination',
                'Выберите хотя бы одно направление',
                function atLeastOneDestination() {
                    const { technologicalMaturity, submitPrizeDestination, submitTnfDestination } = this.parent;

                    if (technologicalMaturity !== TECHNOLOGICAL_MATURITY_TRL_6_9) {
                        return true;
                    }

                    return Boolean(submitPrizeDestination || submitTnfDestination);
                },
            ),
            submitTnfDestination: boolean(),
            ogrn: string(),
            kpp: string(),
            registered_address: string(),
        });
    }, [locale]);

    const debouncedFetchCities = useRef(
        debounce(async (value: string) => {
            const suggestions = await GetCityList({
                query: value,
            });

            setCitySuggestions(
                (suggestions.data ?? []).map((item: any) => ({
                    value: item.city,
                    text: item.city,
                })),
            );
            setShowCitySuggestions(true);
        }, 400),
    ).current;

    const debouncedFetchCompanies = useRef(
        debounce(async (value: string, showSuggestions: boolean) => {
            try {
                const suggestions = await GetCompaniesList({
                    query: value,
                    limit: 3,
                });

                setInnSuggestions(
                    (suggestions.data ?? []).map((item: any) => ({
                        value: item.company,
                        text: item.company,
                        data: item,
                    })),
                );

                setShowInnSuggestions(showSuggestions);
            } catch (error) {
                formik.setFieldError('inn', 'Ошибка проверки ИНН');
            }
        }, 400),
    ).current;

    const handleClose = () => {
        closePopup(APPLY_REGISTRATION_POPUP_NAME);

        // only when popup is finished closing animation
        setTimeout(() => {
            setResponse(null);
            setCurrentStep(HIDE_STEPS_1_AND_2_FOR_DEV ? 3 : 1);
        }, 650);
    };

    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^A-Za-zА-Яа-яЁё\s'-]/g, '');
        setFieldValue('city', value);

        if (value.length >= 2) {
            debouncedFetchCities(value);
        } else {
            setShowCitySuggestions(false);
            debouncedFetchCities.cancel();
        }
    };

    const handleInnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFieldValue('inn', value);

        setIsCompanySelected(false);
        if (value.length >= 2) {
            debouncedFetchCompanies(value, true);
        } else {
            setShowInnSuggestions(false);
            debouncedFetchCompanies.cancel();
        }
    };

    const handleCitySelect = (selectedValue: string) => {
        setFieldValue('city', selectedValue);
        setShowCitySuggestions(false);
    };
    function extractNumberAfterSlash(line: string) {
        const match = line.match(/\/\s*(\d+)\s*$/);
        return match ? match[1] : null;
    }

    const handleInnSelect = (selectedValue: string) => {
        const numberInn = extractNumberAfterSlash(selectedValue);
        if (numberInn) {
            debouncedFetchCompanies(numberInn, false);
            setTimeout(() => {
                setShowInnSuggestions(false);
            }, 500);
        }

        const selectedSuggestion = innSuggestions.find((suggestion) => selectedValue.includes(suggestion.value));

        setIsCompanySelected(true);
        formik.setFieldError('inn', undefined);
        setShowInnSuggestions(false);

        if (selectedSuggestion?.data) {
            setFieldValue('inn', selectedSuggestion.data.inn || '');
            setFieldValue('company', selectedSuggestion.data.company || '');
            setFieldValue('ogrn', selectedSuggestion.data.ogrn || '');
            setFieldValue('kpp', selectedSuggestion.data.kpp || 'none');
            setFieldValue('registered_address', selectedSuggestion.data.registered_address || '');
        }
    };

    const formik = useFormik({
        initialValues: {
            agreementChecked: 0,
            agreementCheckedProcessingPersonalData: 0,
            amount: '',
            city: '',
            company: '',
            customers: '',
            documentUrls: [],
            email: '',
            inn: '',
            isPhysicalPerson: 0,
            kpp: '',
            lastname: '',
            name: '',
            ogrn: '',
            phone: '',
            presentationUrls: [],
            registered_address: '',
            resume: '',
            shortName: '',
            submitPrizeDestination: false,
            submitTnfDestination: false,
            technologicalMaturity: '',
            theme: '',
            themeTrack: '',
            type: '',
        },
        validationSchema: schema,
        validateOnMount: false,
        validateOnChange: true,
        onSubmit: async (values) => {
            setResponse(null);

            const transformedData: ApiProjectRegistrationRequest = {
                name: values.name,
                email: values.email,
                theme: values.theme,
                theme_track: values.themeTrack,
                city: values.city,
                last_name: values.lastname,
                inn: values.inn,
                company: values.company,
                short_project_name: values.shortName,
                sentence_type: values.type,
                readiness_stage: values.technologicalMaturity,
                short_description: values.resume.trim(),
                presentations: values.presentationUrls,
                documents: values.documentUrls,
                phone: Number(formatPhoneHref(values.phone).replace('+', '')),
                registered_address: values.registered_address,
                kpp: values.kpp,
                ogrn: values.ogrn,
                is_physical_person: values.isPhysicalPerson,
                agreementChecked: values.agreementChecked,
                agreementCheckedProcessingPersonalData: values.agreementCheckedProcessingPersonalData,

                url: currentUrl.length > 0 ? currentUrl : '',
                ...Object.fromEntries(
                    Object.entries(getUtmParams).filter(([_, value]) => value), // Фильтруем только непустые значения
                ),
            };

            if (values.technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9 && values.amount.trim()) {
                transformedData.volume_products = values.amount;
            }

            if (values.technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9 && values.customers.trim()) {
                transformedData.customers_consumers = values.customers;
            }

            if (values.technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9 && values.submitPrizeDestination) {
                transformedData.submit_prize = values.submitPrizeDestination;
            }

            if (values.technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9 && values.submitTnfDestination) {
                transformedData.submit_tnf_days = values.submitTnfDestination;
            }

            try {
                const data = await postProjectRegistration(
                    omitEmptyFields(transformedData) as ApiProjectRegistrationRequest,
                );

                setResponse(data);

                if (data.status === 'success') {
                    formik.resetForm();
                }
            } catch (err) {
                if (err instanceof AxiosError) {
                    setResponse({
                        status: 'error',
                        message: err.response?.data.errors?.flat().join('<br>') ?? 'Что-то пошло не так',
                    });
                } else {
                    throw err;
                }
            }
        },
    });
    const {
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        isSubmitting,
        setFieldTouched,
        setFieldValue,
        setFieldError,
        validateField,
    } = formik;

    const uploadPresentationFiles = useCallback(
        async (files: File[]) => {
            const uploadedLinks = await uploadFiles({ files });
            const updated = [...values.presentationUrls, ...uploadedLinks];

            await setFieldValue('presentationUrls', updated, false);
            await setFieldTouched('presentationUrls', true, false);
            await validateField('presentationUrls');
            setFieldError('presentationUrls', undefined);
        },
        [setFieldError, setFieldTouched, setFieldValue, validateField, values.presentationUrls],
    );

    const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
        setFieldTouched(event.target.name, false);
    };

    useEffect(() => {
        scrollToActiveStep();
    }, [currentStep]);

    const nextStep = () => {
        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
            scrollToTop();
            scrollToActiveStep();
        }
    };

    const prevStep = () => {
        const minStep = HIDE_STEPS_1_AND_2_FOR_DEV ? 3 : 1;
        if (currentStep > minStep) {
            setCurrentStep(currentStep - 1);
            scrollToTop();
            scrollToActiveStep();
            setResponse(null);
        }
    };

    const scrollToTop = () => {
        if (formContainerRef.current) {
            formContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const scrollToActiveStep = () => {
        if (stepsRef.current) {
            const steps = stepsRef.current.querySelectorAll<HTMLDivElement>('.form-step-item');
            const scrollIndex = HIDE_STEPS_1_AND_2_FOR_DEV ? 0 : currentStep - 1;
            const activeStep = steps[scrollIndex];
            if (activeStep) {
                stepsRef.current.scrollTo({
                    left: activeStep.offsetLeft - 20,
                    behavior: 'smooth',
                });
            }
        }
    };

    const isStepValid = () => {
        type StepField = keyof AppProjectRegistrationRequest | 'technologicalMaturity' | 'themeTrack';

        const baseRequiredFields: Record<number, StepField[]> = {
            1: ['lastname', 'name', 'email', 'phone', 'inn', 'city'],
            2: ['shortName', 'theme', 'type', 'technologicalMaturity', 'resume', 'presentationUrls'],
            3: ['themeTrack', 'agreementChecked', 'agreementCheckedProcessingPersonalData'],
        };

        const requiredFields = [...(baseRequiredFields[currentStep] ?? [])];
        if (currentStep === 3 && values.technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9) {
            requiredFields.push('amount');
        }

        const fieldsOk = requiredFields.every((field) => {
            const value = values[field as keyof typeof values];
            const hasError = errors[field as keyof typeof errors];
            const isTouched = touched[field as keyof typeof touched];

            if (field === 'presentationUrls') {
                return Array.isArray(value) && value.length > 0 && !hasError;
            }

            if (field === 'inn') {
                if (values.isPhysicalPerson === 1) return true;
                return !errors.inn && isCompanySelected;
            }

            if (field === 'resume') {
                return typeof value === 'string' && value.trim().length >= 30 && !hasError;
            }

            if (typeof value === 'string') {
                return value.trim().length > 0 && !hasError;
            }

            if (typeof value === 'boolean' || typeof value === 'number') {
                const numValue = typeof value === 'boolean' ? (value ? 1 : 0) : value;
                return numValue === 1 && !hasError;
            }

            return !hasError && isTouched;
        });

        if (currentStep === 2 && values.technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9) {
            return (
                fieldsOk &&
                (values.submitPrizeDestination || values.submitTnfDestination) &&
                !errors.submitPrizeDestination
            );
        }

        return fieldsOk;
    };

    const showVolumeAndCustomersFields = values.technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9;
    const showSubmitDestinationFields = values.technologicalMaturity === TECHNOLOGICAL_MATURITY_TRL_6_9;

    return (
        <Popup
            name={APPLY_REGISTRATION_POPUP_NAME}
            data-popup-preset="slide-right"
            className="apply-registration-form-popup apply-registration-form-popup--combined"
            overlay={true}
            containerRef={formContainerRef}
        >
            <button className="menu-popup-close-btn" onClick={handleClose}>
                <CloseSVG />
            </button>
            <AnimatePresence>
                {currentStep > (HIDE_STEPS_1_AND_2_FOR_DEV ? 3 : 1) && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="schema-popup-btns__back-wrapper"
                    >
                        <button className="schema-popup-btns__back" aria-label="Назад" onClick={prevStep}>
                            <ArrSVG />
                        </button>
                        <span className="text-s">Назад</span>
                    </motion.div>
                )}
            </AnimatePresence>
            <h2
                className="apply-registration-form-popup-title text-l"
                dangerouslySetInnerHTML={{
                    __html: tp(
                        'Заявка на участие в Технологических днях TNF <br/> и Всероссийской премии «Территория технологий»',
                    ),
                }}
            ></h2>
            <p
                className="apply-registration-form-popup-subtitle text-s"
                dangerouslySetInnerHTML={{
                    __html: tp(
                        'Вы можете подать один проект одновременно на Премию <br/> и на Технологические дни. На втором шаге у вас будет возможность <br/> выбора, если технологическая зрелость проекта от 6 и выше.',
                    ),
                }}
            ></p>
            <div className="form-steps" ref={stepsRef}>
                {!HIDE_STEPS_1_AND_2_FOR_DEV && (
                    <>
                        <div className={`form-step-item text-xs ${currentStep >= 1 ? '' : 'disabled'}`}>
                            <div className="form-step-item-circle">1</div>
                            <div className="form-step-item-text">Контактные данные</div>
                        </div>
                        <div className={`form-step-item text-xs ${currentStep >= 2 ? '' : 'disabled'}`}>
                            <div className="form-step-item-circle">2</div>
                            <div className="form-step-item-text">Информация о проекте</div>
                        </div>
                    </>
                )}
                <div className={`form-step-item text-xs ${currentStep >= 3 ? '' : 'disabled'}`}>
                    <div className="form-step-item-circle">{HIDE_STEPS_1_AND_2_FOR_DEV ? 1 : 3}</div>
                    <div className="form-step-item-text">Дополнительная информация</div>
                </div>
            </div>

            <form className="apply-registration-form-popup-form" noValidate onSubmit={handleSubmit}>
                <AnimatePresence mode="wait">
                    {currentStep === 1 && !HIDE_STEPS_1_AND_2_FOR_DEV && (
                        <motion.div key="step1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <div className="apply-registration-form-info">
                                <div className="apply-registration-form-info-title text-m">Контактные данные</div>
                                <div className="apply-registration-form-info-text text-s">
                                    <Typografed>
                                        Пожалуйста, заполните контактные и личные данные, которые помогут нам связаться
                                        с вами.
                                    </Typografed>
                                </div>
                            </div>
                            <div className="apply-registration-form-fields">
                                <div className="apply-registration-input-block text-s">
                                    <div className="apply-registration-input-name text-s">Фамилия *</div>
                                    <Input
                                        label=""
                                        type="text"
                                        name="lastname"
                                        placeholder="Иванов"
                                        className="apply-registration-form-popup-input"
                                        id="apply-registration-form-surname"
                                        value={values.lastname}
                                        onChange={(e) => {
                                            const filteredValue = e.target.value.replace(/[^A-Za-zА-Яа-яЁё\s'-]/g, '');
                                            setFieldValue('lastname', filteredValue);
                                        }}
                                        onFocus={onFocus}
                                        onBlur={handleBlur}
                                        message={touched.lastname ? errors.lastname : ''}
                                        valid={!(touched.lastname && errors.lastname)}
                                    />
                                </div>
                                <div className="apply-registration-input-block text-s">
                                    <div className="apply-registration-input-name text-s">Имя *</div>
                                    <Input
                                        label=""
                                        type="text"
                                        name="name"
                                        className="apply-registration-form-popup-input"
                                        id="apply-registration-form-name"
                                        placeholder="Иван"
                                        value={values.name}
                                        onChange={(e) => {
                                            const filteredValue = e.target.value.replace(/[^A-Za-zА-Яа-яЁё\s'-]/g, '');
                                            setFieldValue('name', filteredValue);
                                        }}
                                        onFocus={onFocus}
                                        onBlur={handleBlur}
                                        message={touched.name ? errors.name : ''}
                                        valid={!(touched.name && errors.name)}
                                    />
                                </div>
                                <div className="apply-registration-input-block text-s">
                                    <div className="apply-registration-input-name text-s">Email *</div>
                                    <Input
                                        label=""
                                        type="email"
                                        name="email"
                                        className="apply-registration-form-popup-input"
                                        id="apply-registration-form-email"
                                        placeholder="Введите свой e-mail"
                                        value={values.email}
                                        onChange={handleChange}
                                        onFocus={onFocus}
                                        onBlur={handleBlur}
                                        message={touched.email ? errors.email : ''}
                                        valid={!(touched.email && errors.email)}
                                    />
                                </div>
                                <div className="apply-registration-input-block text-s">
                                    <div className="apply-registration-input-name text-s">Телефон *</div>
                                    <IMaskInput
                                        mask="+7 (000) 000-00-00"
                                        lazy={false}
                                        placeholder="+7 (___) ___-__-__"
                                        value={values.phone}
                                        onAccept={(value: string) => setFieldValue('phone', value)}
                                        onBlur={handleBlur}
                                        onFocus={onFocus}
                                        name="phone"
                                        className="apply-registration-form-popup-input"
                                        id="apply-registration-form-phone"
                                        {...({
                                            render: (ref: any, props: any) => (
                                                <Input
                                                    {...props}
                                                    ref={ref}
                                                    label=""
                                                    message={touched.phone ? errors.phone : ''}
                                                    valid={!(touched.phone && errors.phone)}
                                                />
                                            ),
                                        } as any)}
                                    />
                                </div>
                                <div className="apply-registration-input-block apply-registration-input-block--inn text-s">
                                    <div className="apply-registration-input-name-row">
                                        <div className="apply-registration-input-name text-s">
                                            Название компании / ИНН
                                            {values.isPhysicalPerson !== 1 ? ' *' : ''}
                                        </div>
                                        <Checkbox
                                            className="apply-registration-physical-checkbox"
                                            name="isPhysicalPerson"
                                            checked={values.isPhysicalPerson === 1}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setFieldValue('isPhysicalPerson', checked ? 1 : 0);

                                                if (checked) {
                                                    setFieldValue('inn', '');
                                                    setFieldValue('company', '');
                                                    setFieldValue('ogrn', '');
                                                    setFieldValue('kpp', '');
                                                    setFieldValue('registered_address', '');
                                                    setFieldTouched('inn', false);
                                                    formik.setFieldError('inn', undefined);
                                                    setIsCompanySelected(true);
                                                    setShowInnSuggestions(false);
                                                    debouncedFetchCompanies.cancel();
                                                } else {
                                                    setIsCompanySelected(false);
                                                }
                                            }}
                                        >
                                            Физ. лицо
                                        </Checkbox>
                                    </div>
                                    <AutocompleteInput
                                        label=""
                                        type="text"
                                        name="inn"
                                        id="apply-registration-form-company"
                                        className="apply-registration-form-popup-input"
                                        placeholder="TNF / 7743013902"
                                        value={values.inn}
                                        onChange={handleInnChange}
                                        onFocus={() => {
                                            setShowInnSuggestions(innSuggestions.length > 0);
                                            setFieldTouched('inn', false);
                                        }}
                                        onBlur={() => {
                                            handleBlur;
                                            setTimeout(() => {
                                                setShowInnSuggestions(false);
                                            }, 200);
                                            setIsCompanySelected(false);
                                            handleBlur('inn');
                                        }}
                                        message={
                                            values.isPhysicalPerson !== 1 && !isCompanySelected
                                                ? 'Выберите компанию из списка, всплывающего при вводе названия компании или ИНН'
                                                : ''
                                        }
                                        valid={
                                            values.isPhysicalPerson === 1 ||
                                            (!(touched.inn && errors.inn) && isCompanySelected)
                                        }
                                        setFieldValue={setFieldValue}
                                        showSuggestions={showInnSuggestions}
                                        suggestions={innSuggestions}
                                        onPick={handleInnSelect}
                                        disabled={values.isPhysicalPerson === 1}
                                    />
                                </div>
                                <div className="apply-registration-input-block text-s">
                                    <div className="apply-registration-input-name text-s">Город *</div>
                                    <AutocompleteInput
                                        label=""
                                        type="text"
                                        name="city"
                                        className="apply-registration-form-popup-input"
                                        id="apply-registration-form-city"
                                        placeholder="Москва"
                                        value={values.city}
                                        onChange={handleCityChange}
                                        onFocus={() => {
                                            setShowCitySuggestions(citySuggestions.length > 0);
                                            setFieldTouched('city', false);
                                        }}
                                        onBlur={() => {
                                            handleBlur;
                                            setTimeout(() => {
                                                setShowCitySuggestions(false);
                                            }, 200);
                                        }}
                                        message={touched.city ? errors.city : ''}
                                        valid={!(touched.city && errors.city)}
                                        setFieldValue={setFieldValue}
                                        showSuggestions={showCitySuggestions}
                                        suggestions={citySuggestions}
                                        onPick={handleCitySelect}
                                    />
                                </div>
                                <input type="hidden" name="ogrn" value={values.ogrn} />
                                <input type="hidden" name="kpp" value={values.kpp} />
                                <input type="hidden" name="registered_address" value={values.registered_address} />
                            </div>
                            <div className="form-bottom-info-block">
                                <p className="text-xs form-bottom-info-block-text">
                                    * Поля, обязательные для заполнения.
                                </p>
                                <Button
                                    className="apply-registration-form-button"
                                    type="button"
                                    variant="outline-dark"
                                    onClick={nextStep}
                                    disabled={!isStepValid()}
                                >
                                    Далее
                                </Button>
                            </div>
                        </motion.div>
                    )}

                    {currentStep === 2 && !HIDE_STEPS_1_AND_2_FOR_DEV && (
                        <motion.div key="step2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <div className="apply-registration-form-info">
                                <div className="apply-registration-form-info-title text-m">Информация о проекте</div>
                                <div className="apply-registration-form-info-text text-s">
                                    <Typografed>Опишите проект для технологических дней TNF.</Typografed>
                                </div>
                            </div>
                            <div className="apply-registration-form-fields">
                                <div className="apply-registration-input-block text-s">
                                    <div className="apply-registration-input-name text-s">
                                        Краткое название проекта *
                                    </div>
                                    <Input
                                        label=""
                                        type="text"
                                        name="shortName"
                                        maxLength={100}
                                        id="apply-registration-form-short-name"
                                        className="apply-registration-form-popup-input"
                                        placeholder="Система умного мониторинга энергопотребления"
                                        value={values.shortName}
                                        onChange={handleChange}
                                        onFocus={onFocus}
                                        onBlur={handleBlur}
                                        message={touched.shortName ? errors.shortName : ''}
                                        valid={!(touched.shortName && errors.shortName)}
                                    />
                                </div>
                                <div className="apply-registration-input-block text-s">
                                    <div className="apply-registration-input-name text-s">Тематика *</div>
                                    <FormSelect
                                        id="apply-registration-form-theme"
                                        name="theme"
                                        options={themeOptions}
                                        onChange={handleChange}
                                        placeholder="Выберите тематику"
                                        value={values.theme}
                                        onBlur={handleBlur}
                                        required
                                        className="booking-form__input-field"
                                    />
                                </div>
                                <div className="apply-registration-input-block apply-registration-select text-s">
                                    <div className="apply-registration-input-name text-s">Тип предложения *</div>
                                    <FormSelect
                                        id="apply-registration-form-type"
                                        name="type"
                                        options={typeOptions}
                                        placeholder="Выберите тип предложения"
                                        onChange={(e) => {
                                            const target = e.target as HTMLSelectElement;
                                            setSelectedType(target.value);
                                            formik.handleChange(e);
                                        }}
                                        onBlur={handleBlur}
                                        value={values.type}
                                        required
                                        className="booking-form__input-field"
                                    />
                                </div>
                                <div className="apply-registration-input-block apply-registration-select text-s">
                                    <div className="apply-registration-input-name text-s">
                                        Технологическая зрелость *
                                    </div>
                                    <FormSelect
                                        id="apply-registration-form-stage"
                                        name="technologicalMaturity"
                                        placeholder="Выберите стадию готовности"
                                        options={technologicalMaturityOptions}
                                        onChange={handleChange}
                                        required
                                        onBlur={handleBlur}
                                        value={values.technologicalMaturity}
                                        className="booking-form__input-field"
                                    />
                                </div>
                                <div className="apply-registration-input-block text-s full-width-field">
                                    <div className="apply-registration-input-name text-s">
                                        Краткое резюме, отражающее предложение *
                                    </div>
                                    <Textarea
                                        label=""
                                        name="resume"
                                        id="apply-registration-form-resume"
                                        className="apply-registration-form-popup-textarea"
                                        placeholder="Разработка и внедрение системы умного мониторинга энергопотребления на промышленных объектах с использованием IoT-датчиков и аналитической платформы."
                                        value={values.resume}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        message={touched.resume ? errors.resume : ''}
                                        valid={!(touched.resume && errors.resume)}
                                        required
                                    />
                                </div>
                                <FileDropzone
                                    onUpload={uploadPresentationFiles}
                                    fieldName="presentationUrls"
                                    formik={formik}
                                    label="Презентация *"
                                    allowedFileTypes={['.pdf', 'application/pdf']}
                                    validateMime
                                />

                                <div
                                    className={classNames(
                                        'apply-registration-input-block full-width-field apply-registration-optional-fields',
                                        {
                                            'is-visible': showSubmitDestinationFields,
                                        },
                                    )}
                                >
                                    <div className="apply-registration-optional-fields__inner">
                                        <div className="apply-registration-input-name text-s">
                                            Куда подается проект *
                                        </div>
                                        <div className="checkboxes-wrapper">
                                            <Checkbox
                                                className="agreement-checkbox"
                                                name="submitPrizeDestination"
                                                checked={values.submitPrizeDestination}
                                                onChange={(e) => {
                                                    setFieldTouched('submitPrizeDestination', true, false);
                                                    setFieldTouched('submitTnfDestination', true, false);
                                                    setFieldValue('submitPrizeDestination', e.target.checked);
                                                }}
                                                onBlur={handleBlur}
                                                valid={!errors.submitPrizeDestination}
                                            >
                                                <Typografed>
                                                    Всероссийская премия лучших технологических решений в ТЭК
                                                    «Территория технологий»
                                                </Typografed>
                                            </Checkbox>
                                            <Checkbox
                                                className="agreement-checkbox"
                                                name="submitTnfDestination"
                                                checked={values.submitTnfDestination}
                                                onChange={(e) => {
                                                    setFieldTouched('submitPrizeDestination', true, false);
                                                    setFieldTouched('submitTnfDestination', true, false);
                                                    setFieldValue('submitTnfDestination', e.target.checked);
                                                }}
                                                onBlur={handleBlur}
                                                valid={!errors.submitPrizeDestination}
                                            >
                                                <Typografed>Технологические дни TNF</Typografed>
                                            </Checkbox>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-bottom-info-block">
                                <p className="text-xs form-bottom-info-block-text">
                                    * Поля, обязательные для заполнения.
                                </p>
                                <Button
                                    className="apply-registration-form-button"
                                    type="button"
                                    variant="outline-dark"
                                    onClick={nextStep}
                                    disabled={!isStepValid()}
                                >
                                    Далее
                                </Button>
                            </div>
                        </motion.div>
                    )}

                    {currentStep === 3 && (
                        <motion.div key="step3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <div className="apply-registration-form-info">
                                <div className="apply-registration-form-info-title text-m">
                                    Дополнительная информация
                                </div>
                                <div className="apply-registration-form-info-text text-s">
                                    <Typografed>
                                        Укажите дополнительные сведения и приложите материалы для более подробного
                                        представления проекта.
                                    </Typografed>
                                </div>
                            </div>
                            <div className="apply-registration-form-fields">
                                {showVolumeAndCustomersFields && (
                                    <>
                                        <div className="apply-registration-input-block text-s">
                                            <div className="apply-registration-input-name text-s">
                                                {selectedType === 'Технология'
                                                    ? 'Объем оказанных услуг в год (скв. опер.) *'
                                                    : 'Объем выпускаемой продукции в год (шт.)'}
                                            </div>
                                            <Input
                                                label=""
                                                type="text"
                                                name="amount"
                                                placeholder={
                                                    selectedType === 'Технология'
                                                        ? ''
                                                        : '5000 единиц оборудования для мониторинга энергопотребления'
                                                }
                                                className="apply-registration-form-popup-input"
                                                id="apply-registration-form-amount"
                                                value={values.amount}
                                                onChange={handleChange}
                                                onFocus={onFocus}
                                                onBlur={handleBlur}
                                                valid={!(touched.amount && errors.amount)}
                                            />
                                        </div>
                                        <div className="apply-registration-input-block text-s">
                                            <div className="apply-registration-input-name text-s">
                                                Основные заказчики, потребители
                                            </div>
                                            <Input
                                                label=""
                                                type="text"
                                                name="customers"
                                                className="apply-registration-form-popup-input"
                                                id="apply-registration-form-customers"
                                                placeholder="Промышленные предприятия, энергетические компании, государственные учреждения"
                                                value={values.customers}
                                                onChange={handleChange}
                                                onFocus={onFocus}
                                                onBlur={handleBlur}
                                                message={touched.customers ? errors.customers : ''}
                                                valid={!(touched.customers && errors.customers)}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className="apply-registration-input-block text-s">
                                    <div className="apply-registration-input-name text-s">
                                        Выберите тематический трек *
                                    </div>
                                    <FormSelect
                                        id="apply-registration-form-theme-track"
                                        name="themeTrack"
                                        options={themeTrackOptions}
                                        onChange={handleChange}
                                        placeholder="Выберите тематический трек"
                                        value={values.themeTrack}
                                        onBlur={handleBlur}
                                        required
                                        className="booking-form__input-field"
                                    />
                                </div>
                                <FileDropzone
                                    onUpload={(files) => uploadFiles({ files })}
                                    fieldName="documentUrls"
                                    formik={formik}
                                    label="Дополнительные материалы"
                                    allowedFileTypes={['.pdf', 'application/pdf']}
                                    validateMime
                                />
                            </div>

                            <div className="apply-registration-form-submit-block">
                                <div className="checkboxes-wrapper">
                                    <div className="form-checkbox-wrapper">
                                        <Checkbox
                                            className="agreement-checkbox"
                                            name="agreementChecked"
                                            checked={values.agreementChecked === 1}
                                            onChange={(e) => {
                                                setFieldTouched('agreementChecked', true, false);
                                                handleChange({
                                                    target: {
                                                        name: e.target.name,
                                                        value: e.target.checked ? 1 : 0,
                                                    },
                                                });
                                            }}
                                            onBlur={handleBlur}
                                            valid={!errors.agreementChecked}
                                        >
                                            <span className="contact-us__checkbox-block__text text-xs">
                                                <PersonalDataConsentLabel />
                                            </span>
                                        </Checkbox>
                                    </div>
                                    <div className="form-checkbox-wrapper">
                                        <Checkbox
                                            className="agreement-checkbox"
                                            name="agreementCheckedProcessingPersonalData"
                                            checked={values.agreementCheckedProcessingPersonalData === 1}
                                            onBlur={handleBlur}
                                            valid={!errors.agreementCheckedProcessingPersonalData}
                                            onChange={(e) => {
                                                setFieldTouched('agreementCheckedProcessingPersonalData', true, false);
                                                handleChange({
                                                    target: {
                                                        name: e.target.name,
                                                        value: e.target.checked ? 1 : 0,
                                                    },
                                                });
                                            }}
                                        >
                                            <span className="contact-us__checkbox-block__text text-xs">
                                                Принимаю условия{' '}
                                                <a
                                                    href="/privacy/"
                                                    className="link link--underlined"
                                                    target="_blank"
                                                    rel="noreferrer nofollow"
                                                >
                                                    пользовательского соглашения
                                                </a>
                                            </span>
                                        </Checkbox>
                                        <p className="privacy-checkbox"></p>
                                    </div>
                                </div>

                                <div className="form-navigation">
                                    <Button
                                        type="submit"
                                        variant="outline-dark"
                                        disabled={isSubmitting || !isStepValid()}
                                        className="apply-registration-form-popup-submit"
                                    >
                                        Отправить
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </form>
            <AnimatePresence>
                {response && (
                    <motion.div
                        className="apply-registration-detail-popup-response"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        {response.status === 'success' ? (
                            <>
                                <p className="apply-registration-form-popup-title text-l">
                                    <Typografed>
                                        Ваша заявка сформирована и направлена организаторам. Благодарим вас за
                                        вовлеченность.
                                    </Typografed>
                                </p>
                                <div className="text-s apply-registration-detail-popup-response-text">
                                    <p className="text-s">
                                        <Typografed>
                                            Если у вас остались вопросы — выберите нужное направление и напишите нам.
                                        </Typografed>
                                    </p>
                                    <div className="apply-registration-detail-popup-response-text-block">
                                        <span>Вопросы по премии «Территория технологий»</span>
                                        <Button
                                            tag={Link}
                                            variant="outline-dark"
                                            href="/events/premiya-tec/"
                                            className="text-m"
                                        >
                                            Премия
                                        </Button>
                                    </div>
                                    <div className="apply-registration-detail-popup-response-text-block">
                                        <span>Вопросы по Технологическим дням TNF</span>
                                        <Button
                                            tag={Link}
                                            variant="outline-dark"
                                            href="/events/technological/"
                                            className="text-m"
                                        >
                                            Технологические дни
                                        </Button>
                                    </div>
                                    <div className="apply-registration-detail-popup-response-text-block">
                                        <span>Вопросы по участию в Форуме</span>
                                        <Button tag={Link} variant="outline-dark" href="/contacts/" className="text-m">
                                            Контакты
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-l">{DICT.ERROR_OCCURRED[locale]}</p>
                                {response.message && (
                                    <div
                                        className="text-s apply-registration-form-error-message"
                                        dangerouslySetInnerHTML={{ __html: tp(response.message) }}
                                    ></div>
                                )}
                                <Button type="button" variant="primary-filled" onClick={() => setResponse(null)}>
                                    {DICT.RESEND[locale]}
                                </Button>
                                <a
                                    href={'https://t.me/tnf_oilgasforum_bot'}
                                    className="text-xs"
                                    target="_blank"
                                    rel="noreferrer nofollow"
                                >
                                    {DICT.SUPPORT_SERVICE[locale]}
                                </a>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </Popup>
    );
}

export default ApplyRegistrationPopupCombined;
